// KontrolaPracovnikovView.jsx — stránka týždennej kontroly „Kto pre nás pracuje“ (/kontrola-pracovnikov)
// Mirko 13. 9. 2026: „každý pondelok office kontrolovali, kto pre nás pracuje a či sa stav do piatku nezmenil“; „pondelok ihneď ráno“.
// Pondelok: prejsť zoznam aktívnych pracovníkov (po partiách), každému Pracuje / Už nepracuje (od dátumu), kto chýba → text.
// Piatok: „Bez zmeny od pondelka“ alebo uložiť zmeny. Ukladá POST /api/kontrola-pracovnikov (kto/kedy, audit, karty Trinity).
// „Už nepracuje“ NEdeaktivuje — karta Trinity pre Kašubjakovú (odhlási pracovníka / ukončí pomer, deaktivuje v Zamestnancoch); potvrdenie pred uložením (12.0/3).
import React, { useEffect, useMemo, useState } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import { useNavigate } from 'react-router-dom'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { PageHeader } from '../components/Ui.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'

const hhmm = (x) => (x ? String(x).replace('T', ' ').slice(11, 16) : '')
const dmy = (x) => { if (!x) return ''; const [y, m, d] = String(x).slice(0, 10).split('-'); return `${d}.${m}.${y}` }
const typLabel = (e) => {
  const t = e.employee_type === 'subdodavka' ? 'Subdodávka' : (e.employee_type === 'szco' || /SZ[CČ]O|IČO/i.test(e.uvazok)) ? 'SZČO' : (e.uvazok || 'TPP')
  return t === 'TPP' && e.employee_type === 'employee' ? 'TPP' : t
}

export default function KontrolaPracovnikovView() {
  const navigate = useNavigate()
  const toast = useToast()
  const [stav, setStav] = useState(null)
  const [items, setItems] = useState([])
  const [draft, setDraft] = useState({})          // employee_id → { stav, od }
  useDirtyFlag(Object.keys(draft).length > 0, { key: 'kontrola-pracovnikov', discard: () => { setDraft({}) } })   // straz_v4
  const [chybaju, setChybaju] = useState('')
  const [poznamka, setPoznamka] = useState('')
  const [confirm, setConfirm] = useState(null)    // { typ, bez_zmeny }
  const [busy, setBusy] = useState(false)
  const [filter, setFilter] = useState('')

  const load = () => Promise.all([
    fetchAuth('/api/kontrola-pracovnikov/stav').then(r => r.ok ? r.json() : null),
    fetchAuth('/api/kontrola-pracovnikov/zoznam').then(r => r.ok ? r.json() : { items: [] }),
  ]).then(([s, z]) => {
    setStav(s); const arr = z.items || []; setItems(arr)
    const d = {}; for (const e of arr) d[e.id] = e.datum_vystupu ? { stav: 'nepracuje', od: e.datum_vystupu } : { stav: 'pracuje', od: '' }
    setDraft(d)
  }).catch(e => toast.error('Načítanie zlyhalo: ' + e.message))
  useEffect(() => { load() }, [])

  const typ = stav ? (stav.due === 'piatok' ? 'piatok' : (stav.po && stav.dow >= 5 ? 'piatok' : 'pondelok')) : 'pondelok'
  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const g = new Map()
    for (const e of items) {
      if (q && !(hladaj(e.name, q) || hladaj((e.party_name || e.party || ''), q))) continue
      const k = e.party_name || e.party || 'Bez partie'
      if (!g.has(k)) g.set(k, []); g.get(k).push(e)
    }
    return Array.from(g.entries())
  }, [items, filter])
  const nepracuju = items.filter(e => (draft[e.id] || {}).stav === 'nepracuje')
  const set = (id, patch) => setDraft(d => ({ ...d, [id]: { ...(d[id] || { stav: 'pracuje', od: '' }), ...patch } }))

  const submit = async () => {
    if (!confirm) return
    setBusy(true)
    try {
      const body = { typ: confirm.typ, bez_zmeny: confirm.bez_zmeny ? 1 : 0, chybaju, poznamka,
        polozky: items.map(e => ({ employee_id: e.id, stav: (draft[e.id] || {}).stav || 'pracuje', od: (draft[e.id] || {}).od || '' })) }
      const r = await fetchAuth('/api/kontrola-pracovnikov', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Uloženie zlyhalo'); return }
      toast.success(`Kontrola (${d.typ}) uložená: ${d.pocet_pracuje} pracuje, ${d.pocet_nepracuje} nepracuje${d.neo && d.neo.length ? ', karty Trinity: ' + d.neo.join(', ') : ''}`)
      setConfirm(null); setChybaju(''); setPoznamka(''); load()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  const st = { height: '100%', overflowY: 'auto', boxSizing: 'border-box', padding: SPACE.lg, paddingBottom: 80 }   // scroll v rámci Layoutu (Mirko 13. 9.: „stránka nejde scrollovať“)
  const box = { border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg, padding: SPACE.md, marginBottom: SPACE.md }
  const label = { fontSize: TYPO.sizes.xs, color: COLORS.muted }
  if (!stav) return <div style={st}>Načítavam…</div>
  if (stav.visible === false) return <div style={st}>Kontrolu pracovníkov robí vedúci office; vidí ju vedenie.</div>
  const stavText = stav.po ? `Pondelok: ${stav.po.checked_by} ${dmy(stav.po.datum)} ${hhmm(stav.po.checked_at)} (${stav.po.pocet_pracuje} pracuje, ${stav.po.pocet_nepracuje} nepracuje)` : 'Pondelková kontrola tento týždeň ešte nebola.'
  const piText = stav.pi ? `Piatok: ${stav.pi.checked_by} ${dmy(stav.pi.datum)} ${hhmm(stav.pi.checked_at)}${stav.pi.bez_zmeny ? ' — bez zmeny' : ` — ${stav.pi.pocet_nepracuje} nepracuje`}` : (stav.dow >= 5 ? 'Piatková kontrola ešte nebola.' : '')

  return (
    <div style={st}>
     <div style={{ maxWidth: 1100 }}>
      <PageHeader title="Kto pre nás pracuje — týždenná kontrola" subtitle={`Týždeň ${stav.tyzden} · ${stav.active_count} aktívnych pracovníkov · pondelok ráno: kto pracuje; piatok: zmenil sa stav?`}>
        <BtnGhost onClick={() => navigate('/zamestnanci')}>Zamestnanci (číselník)</BtnGhost>
      </PageHeader>

      <div style={{ ...box, borderLeft: `4px solid ${stav.overdue ? '#dc2626' : (stav.due ? '#f59e0b' : '#16a34a')}` }}>
        <div style={{ fontWeight: 700 }}>{stavText}</div>
        {piText && <div style={{ marginTop: 4 }}>{piText}</div>}
        {stav.nahlasene_vystupy > 0 && <div style={{ marginTop: 4, color: '#b45309' }}>Nahlásené výstupy, ktoré ešte Kašubjaková neuzavrela: {stav.nahlasene_vystupy}</div>}
        <div style={{ marginTop: 6, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          Postup: prejdi zoznam po partiách. Kto už pre nás nepracuje, označ „Už nepracuje" a dátum. Samotné kliknutie nič nemení — až „Uložiť" a potvrdenie.
          Matrix nikoho nedeaktivuje: Kašubjaková dostane kartu v Trinity, odhlási pracovníka, ukončí pomer a deaktivuje ho v Zamestnancoch. Kto v zozname chýba, napíš dole. V piatok stačí „Bez zmeny od pondelka".
          Kontrolu ukladá len vedúci office.
        </div>
      </div>

      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', marginBottom: SPACE.sm, flexWrap: 'wrap' }}>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Hľadať meno / partiu" style={{ padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, width: 260, fontFamily: 'inherit' }} />
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Červené dni = ako dlho o človeku Matrix nemá stopu (Rozpis, Dochádzka, Mzdy). Nad 30 dní preveriť. „Rozpis (plán do)" = má naplánovanú prácu.</span>
      </div>

      {groups.map(([party, arr]) => (
        <div key={party} style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{party} <span style={{ fontWeight: 400, color: COLORS.muted }}>({arr.length})</span></div>
          {arr.map(e => {
            const d = draft[e.id] || { stav: 'pracuje', od: '' }
            const dni = e.dni_bez_stopy
            const dniColor = dni === null ? '#dc2626' : (dni > 30 ? '#dc2626' : (dni > 14 ? '#b45309' : COLORS.muted))
            return (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,2fr) minmax(120px,1.2fr) minmax(150px,1.4fr) auto', gap: 8, alignItems: 'center', padding: '6px 0', borderTop: `1px solid ${COLORS.line}`, background: d.stav === 'nepracuje' ? 'rgba(220,38,38,0.06)' : 'transparent' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{e.name} <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: 400 }}>{typLabel(e)}{e.nationality && e.nationality !== 'SK' ? ' · ' + e.nationality : ''}{e.category ? ' · ' + e.category : ''}</span></div>
                  {e.datum_vystupu && <div style={{ fontSize: TYPO.sizes.xs, color: '#b45309' }}>výstup nahlásený od {dmy(e.datum_vystupu)} ({e.vystup_nahlasil}) — rieši Kašubjaková</div>}
                </div>
                <div style={{ fontSize: TYPO.sizes.xs, color: dniColor }}>{e.stopa ? `${e.stopa_zdroj} ${dmy(e.stopa)}` + (/plán/.test(e.stopa_zdroj) ? '' : ` · ${dni} dní`) : 'bez stopy v Matrixe'}</div>
                <div style={{ display: 'flex', gap: 10, fontSize: TYPO.sizes.sm }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><input type="radio" name={'s' + e.id} checked={d.stav === 'pracuje'} onChange={() => set(e.id, { stav: 'pracuje', od: '' })} /> Pracuje</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#dc2626' }}><input type="radio" name={'s' + e.id} checked={d.stav === 'nepracuje'} onChange={() => set(e.id, { stav: 'nepracuje', od: d.od || stav.today })} /> Už nepracuje</label>
                </div>
                <div>{d.stav === 'nepracuje' && <input type="date" value={d.od || ''} onChange={ev => set(e.id, { od: ev.target.value })} style={{ padding: '4px 6px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit' }} />}</div>
              </div>
            )
          })}
        </div>
      ))}

      <div style={box}>
        <div style={label}>Kto pre nás pracuje, ale v zozname chýba? (meno, partia, od kedy) — vznikne karta v Trinity na doplnenie číselníka</div>
        <textarea value={chybaju} onChange={e => setChybaju(e.target.value)} rows={2} style={{ width: '100%', boxSizing: 'border-box', padding: 8, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit' }} />
        <div style={{ ...label, marginTop: 8 }}>Poznámka ku kontrole (nepovinné)</div>
        <input value={poznamka} onChange={e => setPoznamka(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 8, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit' }} />
      </div>

      {stav.can_do && (
        <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap', alignItems: 'center' }}>
          {typ === 'pondelok'
            ? <BtnAdd onClick={() => setConfirm({ typ: 'pondelok', bez_zmeny: 0 })}>Uložiť pondelkovú kontrolu</BtnAdd>
            : <>
                <BtnAdd onClick={() => setConfirm({ typ: 'piatok', bez_zmeny: 1 })}>Bez zmeny od pondelka</BtnAdd>
                <Btn onClick={() => setConfirm({ typ: 'piatok', bez_zmeny: 0 })}>Uložiť piatkovú kontrolu so zmenami</Btn>
              </>}
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{nepracuju.length ? `Označení „už nepracuje": ${nepracuju.map(e => e.name).join(', ')}` : 'Nikto nie je označený ako „už nepracuje".'}</span>
        </div>
      )}
      {!stav.can_do && <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Kontrolu ukladá vedúci office. Ty ju vidíš, ale neukladáš.</div>}

      {confirm && (
        <div style={{ ...box, marginTop: SPACE.md, borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Potvrdiť uloženie kontroly ({confirm.typ}{confirm.bez_zmeny ? ', bez zmeny od pondelka' : ''})</div>
          <div style={{ fontSize: TYPO.sizes.sm }}>
            Pracuje: <b>{items.length - nepracuju.length}</b> · Už nepracuje: <b>{nepracuju.length}</b>{nepracuju.length ? ' (' + nepracuju.map(e => `${e.name} od ${dmy((draft[e.id] || {}).od)}`).join(', ') + ')' : ''}
            {chybaju.trim() ? <div>Chýbajú v zozname: {chybaju}</div> : null}
            {nepracuju.length ? <div style={{ marginTop: 4, color: '#b45309' }}>Za každého označeného vznikne karta pre Kašubjakovú v Trinity (odhlásenie, ukončenie pomeru). Nikto sa nedeaktivuje automaticky.</div> : null}
          </div>
          <div style={{ display: 'flex', gap: SPACE.sm, marginTop: SPACE.sm }}>
            <BtnAdd onClick={submit} disabled={busy}>{busy ? 'Ukladám…' : 'Potvrdiť a uložiť'}</BtnAdd>
            <BtnGhost onClick={() => setConfirm(null)}>Späť</BtnGhost>
          </div>
        </div>
      )}

      <div style={{ ...box, marginTop: SPACE.lg }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>História kontrol</div>
        {(stav.history || []).length === 0 ? <div style={{ color: COLORS.muted }}>Zatiaľ žiadna kontrola.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
            <thead><tr style={{ textAlign: 'left', color: COLORS.muted }}><th>Týždeň</th><th>Typ</th><th>Dátum</th><th>Kto</th><th>Pracuje</th><th>Nepracuje</th><th>Poznámka</th></tr></thead>
            <tbody>{stav.history.map(h => (
              <tr key={h.id} style={{ borderTop: `1px solid ${COLORS.line}` }}>
                <td>{h.tyzden}</td><td>{h.typ}</td><td>{dmy(h.datum)} {hhmm(h.checked_at)}</td><td>{h.checked_by}</td><td>{h.pocet_pracuje}</td><td style={{ color: h.pocet_nepracuje ? '#dc2626' : undefined }}>{h.pocet_nepracuje}</td><td>{h.bez_zmeny ? 'bez zmeny' : ''}{h.chybaju ? ' chýbajú: ' + h.chybaju : ''}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
     </div>
    </div>
  )
}
